/**
 * DataSourceList - Manage data source connections
 * 
 * A clean list interface for creating, editing, and testing data source connections.
 * Each source has its own test connection button.
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Switch } from "@/shared/ui/switch";
import { ScrollArea } from "@/shared/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { toast } from "sonner";
import {
  Database,
  Table2,
  Plus,
  Trash2,
  Edit,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  XCircle,
  Plug,
  Search,
} from "lucide-react";
import {
  type DataSource,
  type DataSourceType,
  type InfluxDBConfig,
  type PostgreSQLConfig,
  type MySQLConfig,
  DATA_SOURCE_TYPES,
  getDataSourceTypeInfo,
  getConfigDisplayUrl,
  validateDataSourceConfig,
  isInfluxDBConfig,
  isPostgreSQLConfig,
  createDataSource,
  updateDataSource,
  deleteDataSource,
  toggleDataSourceActive,
  testDataSourceConnection,
} from "@/core/api/data-sources";
import type { Json } from "@/integrations/supabase/types";

// Icon mapping for source types
const SourceTypeIcon = ({ type, className }: { type: DataSourceType; className?: string }) => {
  const info = getDataSourceTypeInfo(type);
  switch (info.icon) {
    case 'Table2':
      return <Table2 className={className} />;
    default:
      return <Database className={className} />;
  }
};

interface DataSourceListProps {
  dataSources: DataSource[];
  tenantId: string;
  factoryId: string;
  onSelectSource?: (sourceId: string) => void;
  selectedSourceId?: string;
}

export function DataSourceList({
  dataSources,
  tenantId,
  factoryId,
  onSelectSource,
  selectedSourceId,
}: DataSourceListProps) {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<DataSource | null>(null);
  
  // Form state
  const [formName, setFormName] = useState("");
  const [formType, setFormType] = useState<DataSourceType>("influxdb_v2");
  const [showPassword, setShowPassword] = useState(false);
  
  // InfluxDB fields
  const [influxUrl, setInfluxUrl] = useState("");
  const [influxOrg, setInfluxOrg] = useState("");
  const [influxToken, setInfluxToken] = useState("");
  const [influxBucket, setInfluxBucket] = useState("");
  
  // SQL fields (PostgreSQL/MySQL)
  const [sqlHost, setSqlHost] = useState("");
  const [sqlPort, setSqlPort] = useState("5432");
  const [sqlDatabase, setSqlDatabase] = useState("");
  const [sqlUser, setSqlUser] = useState("");
  const [sqlPassword, setSqlPassword] = useState("");
  const [sqlSsl, setSqlSsl] = useState(true);
  
  // Testing state per source
  const [testingSourceId, setTestingSourceId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; message: string }>>({});

  // Mutations
  const createMutation = useMutation({
    mutationFn: async () => {
      const config = buildConfig();
      return createDataSource({
        tenant_id: tenantId,
        factory_id: factoryId,
        name: formName,
        source_type: formType,
        config,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-sources"] });
      toast.success("Data source created");
      closeDialog();
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to create data source");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editingSource) throw new Error("No source selected");
      const config = buildConfig();
      return updateDataSource(editingSource.id, {
        name: formName,
        config: config as unknown as Json,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-sources"] });
      toast.success("Data source updated");
      closeDialog();
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update data source");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteDataSource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-sources"] });
      toast.success("Data source deleted");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to delete data source");
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => 
      toggleDataSourceActive(id, active),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-sources"] });
    },
  });

  // Build config object from form state
  const buildConfig = (): InfluxDBConfig | PostgreSQLConfig | MySQLConfig => {
    if (formType === "influxdb_v2") {
      return {
        url: influxUrl.trim(),
        org: influxOrg.trim(),
        token: influxToken.trim(),
        bucket: influxBucket.trim() || undefined,
      };
    } else {
      return {
        host: sqlHost.trim(),
        port: parseInt(sqlPort) || (formType === "mysql" ? 3306 : 5432),
        database: sqlDatabase.trim(),
        user: sqlUser.trim(),
        password: sqlPassword.trim(),
        ssl: sqlSsl,
      };
    }
  };

  // Validate form
  const isFormValid = (): boolean => {
    if (!formName.trim()) return false;
    const config = buildConfig();
    const validation = validateDataSourceConfig(formType, config);
    return validation.valid;
  };

  // Open dialog for new or edit
  const openDialog = (source?: DataSource) => {
    if (source) {
      setEditingSource(source);
      setFormName(source.name);
      setFormType(source.source_type);
      
      const config = source.config as Record<string, unknown>;
      if (isInfluxDBConfig(config)) {
        setInfluxUrl(config.url || "");
        setInfluxOrg(config.org || "");
        setInfluxToken(config.token || "");
        setInfluxBucket(config.bucket || "");
      } else if (isPostgreSQLConfig(config)) {
        setSqlHost(config.host || "");
        setSqlPort(String(config.port || 5432));
        setSqlDatabase(config.database || "");
        setSqlUser(config.user || "");
        setSqlPassword(config.password || "");
        setSqlSsl(config.ssl !== false);
      }
    } else {
      setEditingSource(null);
      setFormName("");
      setFormType("influxdb_v2");
      setInfluxUrl("");
      setInfluxOrg("");
      setInfluxToken("");
      setInfluxBucket("");
      setSqlHost("");
      setSqlPort("5432");
      setSqlDatabase("");
      setSqlUser("");
      setSqlPassword("");
      setSqlSsl(true);
    }
    setShowPassword(false);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingSource(null);
  };

  // Test connection for a specific source
  const handleTestConnection = async (sourceId: string) => {
    setTestingSourceId(sourceId);
    // Clear previous result while testing (spinner on button shows progress)
    setTestResults(prev => {
      const next = { ...prev };
      delete next[sourceId];
      return next;
    });
    
    const result = await testDataSourceConnection(sourceId);
    setTestResults(prev => ({ ...prev, [sourceId]: result }));
    setTestingSourceId(null);
    
    if (result.success) {
      toast.success(result.message);
    } else {
      toast.error(result.message);
    }
  };

  // Filter sources by search
  const filteredSources = dataSources.filter(ds => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const url = getConfigDisplayUrl(ds.config, ds.source_type);
    return (
      ds.name.toLowerCase().includes(q) ||
      ds.source_type.toLowerCase().includes(q) ||
      url.toLowerCase().includes(q)
    );
  });

  return (
    <>
      <div className="flex flex-col h-full">
        {/* Search and Add */}
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search data sources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button onClick={() => openDialog()}>
            <Plus className="h-4 w-4 mr-2" />
            Add Source
          </Button>
        </div>

        {/* Empty State */}
        {dataSources.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Database className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-lg mb-1">No Data Sources</h3>
            <p className="text-muted-foreground text-sm mb-4 max-w-[300px]">
              Connect to InfluxDB, PostgreSQL, or MySQL to start mapping data to your factory structure.
            </p>
            <Button onClick={() => openDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Data Source
            </Button>
          </div>
        ) : (
          <ScrollArea className="flex-1">
            <div className="space-y-3">
              {filteredSources.map((ds) => {
                const typeInfo = getDataSourceTypeInfo(ds.source_type);
                const testResult = testResults[ds.id];
                const isTesting = testingSourceId === ds.id;
                const isSelected = selectedSourceId === ds.id;

                return (
                  <div
                    key={ds.id}
                    className={`p-4 rounded-lg border transition-all cursor-pointer ${
                      isSelected ? "ring-2 ring-primary border-primary" : "hover:border-primary/50"
                    } ${!ds.is_active ? "opacity-60" : ""}`}
                    onClick={() => onSelectSource?.(ds.id)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className={`p-2 rounded-lg ${typeInfo.bgColor}`}>
                        <SourceTypeIcon type={ds.source_type} className={`h-5 w-5 ${typeInfo.color}`} />
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium truncate">{ds.name}</h4>
                          <Badge variant="outline" className="text-xs shrink-0">
                            {typeInfo.name}
                          </Badge>
                          {ds.is_active ? (
                            <Badge variant="outline" className="text-success border-success/30 text-xs shrink-0">
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground text-xs shrink-0">
                              Inactive
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {getConfigDisplayUrl(ds.config, ds.source_type)}
                        </p>
                        
                        {/* Test Result */}
                        {testResult && (
                          <div className={`flex items-center gap-1 mt-2 text-xs ${
                            testResult.success ? "text-success" : "text-destructive"
                          }`}>
                            {testResult.success ? (
                              <CheckCircle2 className="h-3 w-3" />
                            ) : (
                              <XCircle className="h-3 w-3" />
                            )}
                            {testResult.message}
                          </div>
                        )}
                      </div>

                      {/* Toggle */}
                      <Switch
                        checked={ds.is_active}
                        onCheckedChange={(checked) => {
                          toggleMutation.mutate({ id: ds.id, active: checked });
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTestConnection(ds.id);
                        }}
                        disabled={isTesting}
                      >
                        {isTesting ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <Plug className="h-3 w-3 mr-1" />
                        )}
                        Test
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDialog(ds);
                        }}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm("Delete this data source? This will also remove all field mappings.")) {
                            deleteMutation.mutate(ds.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSource ? "Edit Data Source" : "Add Data Source"}
            </DialogTitle>
            <DialogDescription>
              Configure your database connection settings
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="ds-name">Name</Label>
              <Input
                id="ds-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g., Production Database"
              />
            </div>

            {/* Type Selector (only when creating) */}
            {!editingSource && (
              <div className="space-y-2">
                <Label>Type</Label>
                <div className="grid grid-cols-3 gap-2">
                  {DATA_SOURCE_TYPES.map((type) => (
                    <Button
                      key={type.id}
                      type="button"
                      variant={formType === type.id ? "default" : "outline"}
                      className="flex-col h-auto py-3 gap-1"
                      onClick={() => {
                        setFormType(type.id);
                        if (type.id === "mysql") setSqlPort("3306");
                        else if (type.id === "postgresql") setSqlPort("5432");
                      }}
                    >
                      <SourceTypeIcon type={type.id} className="h-5 w-5" />
                      <span className="text-xs">{type.name}</span>
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* InfluxDB Config */}
            {formType === "influxdb_v2" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="influx-url">URL</Label>
                  <Input
                    id="influx-url"
                    value={influxUrl}
                    onChange={(e) => setInfluxUrl(e.target.value)}
                    placeholder="https://influx.example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="influx-org">Organization</Label>
                  <Input
                    id="influx-org"
                    value={influxOrg}
                    onChange={(e) => setInfluxOrg(e.target.value)}
                    placeholder="my-org"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="influx-token">API Token</Label>
                  <div className="relative">
                    <Input
                      id="influx-token"
                      type={showPassword ? "text" : "password"}
                      value={influxToken}
                      onChange={(e) => setInfluxToken(e.target.value)}
                      placeholder="Your InfluxDB API token"
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="influx-bucket">Default Bucket (optional)</Label>
                  <Input
                    id="influx-bucket"
                    value={influxBucket}
                    onChange={(e) => setInfluxBucket(e.target.value)}
                    placeholder="production"
                  />
                </div>
              </>
            )}

            {/* PostgreSQL/MySQL Config */}
            {(formType === "postgresql" || formType === "mysql") && (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="sql-host">Host</Label>
                    <Input
                      id="sql-host"
                      value={sqlHost}
                      onChange={(e) => setSqlHost(e.target.value)}
                      placeholder="db.example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sql-port">Port</Label>
                    <Input
                      id="sql-port"
                      value={sqlPort}
                      onChange={(e) => setSqlPort(e.target.value)}
                      placeholder={formType === "mysql" ? "3306" : "5432"}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sql-database">Database</Label>
                  <Input
                    id="sql-database"
                    value={sqlDatabase}
                    onChange={(e) => setSqlDatabase(e.target.value)}
                    placeholder="production"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sql-user">Username</Label>
                  <Input
                    id="sql-user"
                    value={sqlUser}
                    onChange={(e) => setSqlUser(e.target.value)}
                    placeholder={formType === "mysql" ? "root" : "postgres"}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sql-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="sql-password"
                      type={showPassword ? "text" : "password"}
                      value={sqlPassword}
                      onChange={(e) => setSqlPassword(e.target.value)}
                      placeholder="Database password"
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="sql-ssl"
                    checked={sqlSsl}
                    onCheckedChange={setSqlSsl}
                  />
                  <Label htmlFor="sql-ssl">Use SSL/TLS</Label>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              onClick={() => editingSource ? updateMutation.mutate() : createMutation.mutate()}
              disabled={!isFormValid() || createMutation.isPending || updateMutation.isPending}
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {editingSource ? "Save Changes" : "Add Source"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
